import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Download, RefreshCw } from "lucide-react";
import { toast } from "sonner";

type ConsentEntry = {
  id: string;
  user_id: string | null;
  phone: string;
  consent_source: string;
  consent_text: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
};

const SOURCES = [
  { value: "all", label: "All sources" },
  { value: "signup_alerts", label: "Signup — Alerts" },
  { value: "signup_marketing", label: "Signup — Marketing" },
  { value: "booking_acknowledgment", label: "Booking acknowledgment" },
];

const SmsConsentLog = () => {
  const [entries, setEntries] = useState<ConsentEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [phone, setPhone] = useState("");
  const [source, setSource] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("sms_consent_log")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(1000);
    if (error) {
      toast.error("Failed to load consent log");
    } else {
      setEntries((data ?? []) as ConsentEntry[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return entries.filter((e) => {
      if (phone && !e.phone.toLowerCase().includes(phone.toLowerCase())) return false;
      if (source !== "all" && e.consent_source !== source) return false;
      if (startDate && new Date(e.created_at) < new Date(startDate)) return false;
      if (endDate) {
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        if (new Date(e.created_at) > end) return false;
      }
      return true;
    });
  }, [entries, phone, source, startDate, endDate]);

  const exportCsv = () => {
    if (filtered.length === 0) {
      toast.error("Nothing to export");
      return;
    }
    const headers = [
      "timestamp",
      "phone",
      "source",
      "consent_text",
      "ip_address",
      "user_agent",
      "user_id",
    ];
    const escape = (v: string | null) =>
      v == null ? "" : `"${String(v).replace(/"/g, '""')}"`;
    const rows = filtered.map((e) =>
      [
        e.created_at,
        e.phone,
        e.consent_source,
        e.consent_text,
        e.ip_address,
        e.user_agent,
        e.user_id,
      ]
        .map(escape)
        .join(",")
    );
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `sms-consent-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setPhone("");
    setSource("all");
    setStartDate("");
    setEndDate("");
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl italic">SMS Consent Log</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Audit trail of every SMS opt-in for A2P 10DLC compliance.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            Refresh
          </Button>
          <Button size="sm" onClick={exportCsv}>
            <Download size={14} />
            Export CSV
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-4 border border-border rounded-md bg-card">
        <div className="space-y-1">
          <Label htmlFor="phone-filter" className="text-xs uppercase tracking-wider">Phone</Label>
          <Input
            id="phone-filter"
            placeholder="Search phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs uppercase tracking-wider">Source</Label>
          <Select value={source} onValueChange={setSource}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SOURCES.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  {s.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label htmlFor="start-date" className="text-xs uppercase tracking-wider">From</Label>
          <Input
            id="start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="end-date" className="text-xs uppercase tracking-wider">To</Label>
          <Input
            id="end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        <div className="md:col-span-4 flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {filtered.length} of {entries.length} entries
          </p>
          <Button variant="ghost" size="sm" onClick={clearFilters}>
            Clear filters
          </Button>
        </div>
      </div>

      <div className="border border-border rounded-md overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Timestamp</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>IP</TableHead>
              <TableHead>User Agent</TableHead>
              <TableHead>Consent Text</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  Loading…
                </TableCell>
              </TableRow>
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                  No consent entries found.
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((e) => (
                <TableRow key={e.id}>
                  <TableCell className="whitespace-nowrap text-xs">
                    {new Date(e.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs font-mono">{e.phone}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">{e.consent_source}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs font-mono">
                    {e.ip_address ?? "—"}
                  </TableCell>
                  <TableCell className="text-xs max-w-[200px] truncate" title={e.user_agent ?? ""}>
                    {e.user_agent ?? "—"}
                  </TableCell>
                  <TableCell className="text-xs max-w-[400px]">
                    <details>
                      <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                        View text
                      </summary>
                      <p className="mt-2 whitespace-pre-wrap">{e.consent_text}</p>
                    </details>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default SmsConsentLog;
